import { AtsJobVacancyStatusEnum } from '@/generated/graphql/types';
import { Details } from '@/shared/components/candidate-form/components/details';
import {
  Form,
  type TFormSubmitResult,
} from '@/shared/components/candidate-form/components/form';
import type { TCandidateForm } from '@/shared/components/candidate-form/types';
import { useAmplitude } from '@crehana/analytics';
import { AtsCandidateFormVisibilityTypeEnum } from '@crehana/compass.knowledgehub/types';
import { useCallback, useEffect, useState } from 'react';
import { ApplicationFormStatus } from '../../shared/application-form-status';
import { useCreateJobApplicantProfile } from '../hooks/create-job-applicant-profile';
import { useTrackVacancyVisit } from '../hooks/use-track-vacancy-visit';
import { useAddResourceBundle } from '../i18n/use-add-resource-bundle';
import { useTranslationPublicCandidateForm } from '../i18n/use-translations';
import type {
  TPageLoaderOrganizationData,
  TPageLoaderVacancyData,
} from '../types';

export const CandidateAnswerForm = ({
  vacancy: jobVacancyPublicInfo,
  organization,
}: {
  vacancy: TPageLoaderVacancyData;
  organization: TPageLoaderOrganizationData;
}) => {
  useAddResourceBundle();
  const { logEvent } = useAmplitude();
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    logEvent({
      event: 'Render Candidate Form',
      data: {
        url: window.location.href,
        product: 'ATS',
      },
    });
  }, []);

  const { t } = useTranslationPublicCandidateForm();

  const [isPublished, setIsPublished] = useState(false);

  const organizationId = jobVacancyPublicInfo?.centralized_organization_id;

  // Track vacancy visit
  const [trackVacancyVisit] = useTrackVacancyVisit();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run once when vacancy/org changes
  useEffect(() => {
    if (!jobVacancyPublicInfo?.id || !organizationId) {
      return;
    }

    trackVacancyVisit({
      variables: {
        input: {
          vacancy_id: jobVacancyPublicInfo.id.toString(),
          centralized_organization_id: organizationId.toString(),
        },
      },
    });
  }, [jobVacancyPublicInfo?.id, organizationId]);

  // Create job applicant profile
  const [createJobApplicantProfile, { loading }] =
    useCreateJobApplicantProfile();

  // Get the personal info map for linkedin visibility check
  const personalInfoMap = Object.fromEntries(
    jobVacancyPublicInfo?.personal_info?.map(item => [
      item.label,
      item.visibility,
    ]) || [],
  );

  const handleSubmitForm = useCallback(
    async (data: TCandidateForm): Promise<TFormSubmitResult> => {
      const result = await createJobApplicantProfile({
        variables: {
          input: {
            vacancy_id: Number(jobVacancyPublicInfo.id),
            first_name: data.name,
            last_name: data.last_name,
            email: data.email,
            phone: data.number_phone,
            country: data.country,
            file_cv_id: parseInt(data.resume, 10),
            linkedin_url:
              personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.linkedin_url,
            origin_candidates:
              personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.candidate_source,
            disability:
              personalInfoMap['PERSONAL_INFO_DISABILITY'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.disability,
            gender:
              personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.gender,
            answers: data.answers.map(answer => ({
              answer_value: answer.answer_value.value,
              question_id: answer.question_id,
              question_name: answer.question_name,
              question_type: answer.question_type,
            })),
          },
        },
      });

      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'CreateJobApplicantProfileSuccess'
      ) {
        setIsPublished(true);
        return { success: true };
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationApplicantEmailExistsError'
      ) {
        return { emailExists: true };
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationApplicantInvalidLinkedinError'
      ) {
        return { invalidLinkedin: true };
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationValidationExistsError'
      ) {
        return { validationError: true };
      }

      return { error: true };
    },
    [createJobApplicantProfile, jobVacancyPublicInfo.id, personalInfoMap],
  );

  // Display a not found status if the job vacancy is not found
  if (
    (jobVacancyPublicInfo && !jobVacancyPublicInfo.id) ||
    jobVacancyPublicInfo.is_deleted ||
    jobVacancyPublicInfo.state === AtsJobVacancyStatusEnum.IsDraft
  ) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_NOT_FOUND')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_NOT_FOUND')}
          type="not-found"
        />
      </div>
    );
  }

  // Display a closed status if the job vacancy is closed
  if (
    jobVacancyPublicInfo?.state === AtsJobVacancyStatusEnum.IsClosed ||
    jobVacancyPublicInfo.is_paused
  ) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_CLOSED')}
          subtitle={t('CANDIDATE_FORM_STATUS_SUBTITLE_CLOSED')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_CLOSED')}
          type="close"
        />
      </div>
    );
  }

  // Display a success status if the application form is published
  if (isPublished) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_SUCCESS')}
          subtitle={t('CANDIDATE_FORM_STATUS_SUBTITLE_SUCCESS')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_SUCCESS')}
          type="success"
        />
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center">
      {/**Show the job vacancy details */}
      <div className="tw-w-full">
        <Details
          type="external"
          jobVacancyPublicInfo={jobVacancyPublicInfo}
          name={organization.name || ''}
          logo={organization.logo || ''}
        />
      </div>
      {/**Show the application form */}
      <div
        id="application-form"
        className="tw-w-full tw-flex tw-justify-center tw-px-3.2 md:tw-px-[9.2rem] lg:tw-px-[32.4rem] tw-py-3.2 md:tw-py-6.4 lg:tw-py-7.2"
        style={{
          background:
            'linear-gradient(90deg, #F2F3F5 0%, rgba(240, 242, 255, 0.90) 100%)',
        }}
      >
        <Form
          type="external"
          jobVacancyPublicInfo={jobVacancyPublicInfo}
          organizationId={organizationId || 0}
          preview={false}
          onSubmitForm={handleSubmitForm}
          loading={loading}
        />
      </div>
    </div>
  );
};
